import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkOrders({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete } = usePermissions(user);
  const [workOrders, setWorkOrders] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDetails, setViewDetails] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [woToDelete, setWoToDelete] = useState(null);
  
  // Filter states
  const [selectedStatus, setSelectedStatus] = useState('PENDING');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Form states
  const woType = 'INTERNAL'; // Always internal
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleOptions, setArticleOptions] = useState([]);
  const [description, setDescription] = useState('');
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [savingWO, setSavingWO] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchWorkOrders();
    fetchArticles();
    
    // Auto-refresh when tab becomes visible (user comes back from sampling tracker)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchWorkOrders();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const articleOpts = response.data.map(article => ({
        value: article.article_code,
        label: `${article.article_code} - ${article.category || ''}`,
        data: article
      }));
      
      setArticleOptions(articleOpts);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to fetch articles');
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      toast.error('Failed to fetch work orders');
    }
  };

  // Auto-refresh every 5 seconds, but pause when any dialog is open
  const isUserEditing = formOpen || viewOpen || deleteDialogOpen;
  useAutoRefresh(fetchWorkOrders, 5, isUserEditing);

  const handleArticleSelect = async (articleCode) => {
    if (!articleCode) {
      setSelectedArticle(null);
      return;
    }

    setFormError(''); // Clear any previous errors
    setLoadingArticle(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs/by-article/${articleCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedArticle(response.data);
      setDescription(`Work Order for ${response.data.article_code} - ${response.data.category || ''}`);
    } catch (error) {
      console.error('Error fetching article details:', error);
      toast.error('Failed to fetch article details');
    } finally {
      setLoadingArticle(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedArticle) {
      toast.error('Please select an LN-X code');
      return;
    }

    if (!description) {
      toast.error('Please enter description');
      return;
    }

    setSavingWO(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare items array with fabrics, trims, and packaging
      const items = [];
      
      // Add fabrics
      if (selectedArticle.fabrics && selectedArticle.fabrics.length > 0) {
        selectedArticle.fabrics.forEach(fabric => {
          items.push({
            type: 'FABRIC',
            code: fabric.fabric_code || fabric.code,
            color_code: fabric.color_code,
            color_name: fabric.color_name,
            consumption: fabric.consumption,
            width: fabric.width,
            gsm: fabric.gsm,
            composition: fabric.composition,
            fabric_type: fabric.fabric_type,
            unit: 'meters'
          });
        });
      }
      
      // Add trims
      if (selectedArticle.trims && selectedArticle.trims.length > 0) {
        selectedArticle.trims.forEach(trim => {
          items.push({
            type: 'TRIM',
            code: trim.trim_code || trim.code,
            color_code: trim.color_code,
            color_name: trim.color_name,
            quantity: trim.quantity,
            unit: 'units'
          });
        });
      }
      
      // Add packaging
      if (selectedArticle.packaging && selectedArticle.packaging.length > 0) {
        selectedArticle.packaging.forEach(pkg => {
          items.push({
            type: 'PACKAGING',
            code: pkg.packaging_code || pkg.code,
            description: pkg.description,
            quantity: pkg.quantity,
            unit: 'units'
          });
        });
      }

      // Check if an internal work order already exists for this article code
      if (woType === 'INTERNAL') {
        const existingResponse = await axios.get(`${API}/work-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const existingInternalWO = existingResponse.data.find(
          wo => wo.article_code === selectedArticle.article_code && wo.wo_type === 'INTERNAL'
        );
        
        if (existingInternalWO) {
          setFormError(`An internal work order (${existingInternalWO.wo_number}) already exists for article ${selectedArticle.article_code}. Please delete it first to create a new one.`);
          return;
        }
      }

      const response = await axios.post(
        `${API}/work-orders`,
        {
          wo_type: woType,
          article_code: selectedArticle.article_code,
          description: description,
          items: items,
          article_data: selectedArticle,
          pom_data: selectedArticle.pom_data || {},
          images: selectedArticle.images || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add the newly created WO directly to the list
      const newWO = response.data;
      setWorkOrders(prevWOs => [newWO, ...prevWOs]);

      toast.success('Work order created successfully');
      
      // Now close the form and reset
      setFormOpen(false);
      setWoType('INTERNAL');
      setSelectedArticle(null);
      setDescription('');
    } catch (error) {
      console.error('Error creating work order:', error);
      toast.error('Failed to create work order');
    } finally {
      setSavingWO(false);
    }
  };

  // Enrich WO with live Fashion Design data
  const enrichWOWithLiveData = async (wo, token) => {
    try {
      // If WO has Fashion Design data, enrich it with live master data
      if (wo.fashion_design_data) {
        const enrichedFD = { ...wo.fashion_design_data };

        // Enrich fabrics with live data
        if (enrichedFD.fabrics && enrichedFD.fabrics.length > 0) {
          const fabricsResponse = await axios.get(`${API}/fabric-master`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          enrichedFD.fabrics = enrichedFD.fabrics.map(fabric => {
            const masterFabric = fabricsResponse.data.find(f => f.ln_code === fabric.fabric_code);
            if (masterFabric) {
              const masterColor = masterFabric.colors?.find(c => c.color_code === fabric.color_code);
              if (masterColor) {
                return {
                  ...fabric,
                  color_name: masterColor.color_name,
                  width: masterFabric.width,
                  gsm: masterFabric.gsm,
                  composition: masterFabric.composition,
                  price_per_meter: masterColor.price_per_meter
                };
              }
            }
            return fabric;
          });
        }

        // Enrich trims with live data
        if (enrichedFD.trims && enrichedFD.trims.length > 0) {
          const trimsResponse = await axios.get(`${API}/trims-master`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const packagingResponse = await axios.get(`${API}/packaging-master`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          enrichedFD.trims = enrichedFD.trims.map(trim => {
            let masterTrim = trimsResponse.data.find(t => t.ln_z_code === trim.trim_code);
            if (!masterTrim) {
              masterTrim = packagingResponse.data.find(p => p.ln_y_code === trim.trim_code);
            }
            
            if (masterTrim) {
              const masterColor = masterTrim.colors?.find(c => c.color_code === trim.color_code);
              if (masterColor) {
                return {
                  ...trim,
                  color_name: masterColor.color_name,
                  price: masterColor.price
                };
              }
            }
            return trim;
          });
        }

        return { ...wo, fashion_design_data: enrichedFD };
      }
      return wo;
    } catch (error) {
      console.error('Error enriching WO with live data:', error);
      return wo;
    }
  };

  const handleView = async (wo) => {
    // Fetch full work order data with all details and LIVE enrichment
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${wo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich with live data
      const enrichedWO = await enrichWOWithLiveData(response.data, token);
      setViewDetails(enrichedWO);
      setViewOpen(true);
    } catch (error) {
      console.error('Error fetching work order details:', error);
      toast.error('Failed to fetch work order details');
    }
  };

  const handleDeleteClick = (wo) => {
    setWoToDelete(wo);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!woToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-orders/${woToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Work order deleted successfully');
      setDeleteDialogOpen(false);
      setWoToDelete(null);
      await fetchWorkOrders(); // Wait for list to refresh
    } catch (error) {
      console.error('Error deleting work order:', error);
      toast.error('Failed to delete work order');
    }
  };


  const handlePrintWO = async (wo) => {
    // Fetch full work order data with article_data and items for printing
    let fullWO = wo;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${wo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fullWO = response.data;
    } catch (error) {
      console.error('Error fetching full work order:', error);
      toast.error('Failed to fetch work order details');
      return;
    }
    
    // Check which sections exist
    const hasPOM = fullWO.article_data && fullWO.article_data.poms && fullWO.article_data.poms.length > 0;
    const hasInspirationImage = fullWO.article_data && fullWO.article_data.inspiration_image;
    const hasSketchFront = fullWO.article_data && fullWO.article_data.sketch_front_image;
    const hasSketchBack = fullWO.article_data && fullWO.article_data.sketch_back_image;
    const hasFinishedGood = fullWO.article_data && fullWO.article_data.finished_good_image;
    
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Work Order - ${fullWO.wo_number}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: 'Arial', sans-serif;
              color: #000;
              margin: 0;
              padding: 0;
              background: white;
            }
            .page {
              page-break-after: always;
              padding: 20px;
              background: white;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              border-bottom: 2px solid #000;
              padding-bottom: 15px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
            }
            .header p {
              margin: 5px 0 0 0;
              font-size: 14px;
            }
            .header img {
              max-width: 200px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
              margin-bottom: 20px;
            }
            .info-item label {
              display: block;
              font-size: 10px;
              text-transform: uppercase;
              color: #666;
            }
            .info-item div {
              font-size: 14px;
              font-weight: bold;
            }
            .section {
              margin-bottom: 20px;
              border: 2px solid #000;
              padding: 15px;
            }
            .section-title {
              font-size: 14px;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 15px;
            }
            .item-card {
              background: #f5f5f5;
              border: 1px solid #ccc;
              padding: 10px;
              margin-bottom: 10px;
            }
            .item-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              margin-bottom: 8px;
            }
            .item-field label {
              font-size: 9px;
              text-transform: uppercase;
              color: #666;
            }
            .item-field div {
              font-size: 11px;
              font-weight: bold;
            }
            .pom-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            .pom-item {
              background: #f5f5f5;
              border: 1px solid #ccc;
              padding: 10px;
              display: flex;
              justify-content: space-between;
            }
            .image-page {
              text-align: center;
              padding: 20px;
              page-break-after: always;
            }
            .image-page:last-child {
              page-break-after: auto;
            }
            .image-page h2 {
              font-size: 18px;
              margin-bottom: 20px;
            }
            .image-page img {
              max-width: 100%;
              max-height: 800px;
              object-fit: contain;
            }
            .comments-box {
              background: #f9f9f9;
              border: 2px solid #000;
              padding: 15px;
              margin-top: 10px;
              text-align: left;
            }
            .comments-box pre {
              white-space: pre-wrap;
              word-wrap: break-word;
              font-family: inherit;
              font-size: 12px;
              line-height: 1.6;
              margin: 0;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .page {
                page-break-after: always;
              }
              .page:last-child {
                page-break-after: auto;
              }
              .image-page {
                page-break-after: always;
              }
              .image-page:last-child {
                page-break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          <!-- Page 1: Main Details -->
          <div class="page">
            <div class="header">
              <div>
                <h1>INTERNAL WORK ORDER</h1>
                <p>${fullWO.wo_number}</p>
              </div>
              <div>
                ${fullWO.wo_barcode ? `<img src="${fullWO.wo_barcode}" alt="Barcode" />` : ''}
              </div>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <label>WO Number</label>
                <div>${fullWO.wo_number}</div>
              </div>
              <div class="info-item">
                <label>Type</label>
                <div>${fullWO.wo_type}</div>
              </div>
              <div class="info-item">
                <label>Article Code</label>
                <div>${fullWO.article_code || '-'}</div>
              </div>
              <div class="info-item">
                <label>Status</label>
                <div>${fullWO.status}</div>
              </div>
              <div class="info-item">
                <label>Created</label>
                <div>${new Date(fullWO.created_at).toLocaleString()}</div>
              </div>
              <div class="info-item">
                <label>Created By</label>
                <div>${fullWO.created_by || '-'}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Description</div>
              <p>${fullWO.description}</p>
            </div>

            ${fullWO.items && fullWO.items.filter(i => i.type === 'FABRIC').length > 0 ? `
            <div class="section">
              <div class="section-title">Fabrics Required</div>
              ${fullWO.items.filter(i => i.type === 'FABRIC').map(item => `
                <div class="item-card">
                  <div class="item-grid">
                    <div class="item-field">
                      <label>Fabric Code</label>
                      <div>${item.code}</div>
                    </div>
                    <div class="item-field">
                      <label>Color Code</label>
                      <div>${item.color_code}</div>
                    </div>
                    <div class="item-field">
                      <label>Color Name</label>
                      <div>${item.color_name}</div>
                    </div>
                    <div class="item-field">
                      <label>Consumption</label>
                      <div>${item.consumption} ${item.unit}</div>
                    </div>
                  </div>
                  <div class="item-grid">
                    <div class="item-field">
                      <label>Composition</label>
                      <div>${item.composition || '-'}</div>
                    </div>
                    <div class="item-field">
                      <label>Width / GSM</label>
                      <div>${item.width || '-'} / ${item.gsm || '-'}</div>
                    </div>
                    <div class="item-field">
                      <label>Fabric Type</label>
                      <div>${item.fabric_type || '-'}</div>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
            ` : ''}

            ${fullWO.items && fullWO.items.filter(i => i.type === 'TRIM').length > 0 ? `
            <div class="section">
              <div class="section-title">Trims & Accessories Required</div>
              ${fullWO.items.filter(i => i.type === 'TRIM').map(item => `
                <div class="item-card">
                  <div class="item-grid">
                    <div class="item-field">
                      <label>Trim Code</label>
                      <div>${item.code}</div>
                    </div>
                    <div class="item-field">
                      <label>Color Code</label>
                      <div>${item.color_code}</div>
                    </div>
                    <div class="item-field">
                      <label>Color Name</label>
                      <div>${item.color_name}</div>
                    </div>
                    <div class="item-field">
                      <label>Quantity</label>
                      <div>QTY ${item.quantity}</div>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
            ` : ''}

            ${fullWO.items && fullWO.items.filter(i => i.type === 'PACKAGING').length > 0 ? `
            <div class="section">
              <div class="section-title">Packaging Required</div>
              ${fullWO.items.filter(i => i.type === 'PACKAGING').map(item => `
                <div class="item-card" style="display: flex; justify-content: space-between;">
                  <div><strong>${item.code}</strong> - ${item.description}</div>
                  <div><strong>${item.quantity} ${item.unit}</strong></div>
                </div>
              `).join('')}
            </div>
            ` : ''}
          </div>

          ${hasPOM ? `
          <!-- Page 2: POM and Comments -->
          <div class="page">
            <div class="section">
              <div class="section-title">Points of Measurement (For Sampler)</div>
              <div class="pom-grid">
                ${fullWO.article_data.poms.map(pom => `
                  <div class="pom-item">
                    <div><strong>SIZE ${pom.size}</strong> - ${pom.point_name}</div>
                    <div><strong>${pom.measurement} ${pom.uom}</strong></div>
                  </div>
                `).join('')}
              </div>
            </div>
            
            ${fullWO.article_data.comments ? `
            <div class="section">
              <div class="section-title">Comments</div>
              <div class="comments-box">
                <pre>${fullWO.article_data.comments}</pre>
              </div>
            </div>
            ` : ''}
          </div>
          ` : fullWO.article_data?.comments ? `
          <!-- Comments Only Page -->
          <div class="page">
            <div class="section">
              <div class="section-title">Comments</div>
              <div class="comments-box">
                <pre>${fullWO.article_data.comments}</pre>
              </div>
            </div>
          </div>
          ` : ''}

          ${hasInspirationImage ? `
          <!-- Inspiration Image Page -->
          <div class="image-page">
            <h2>INSPIRATION IMAGE</h2>
            <img src="${fullWO.article_data.inspiration_image}" alt="Inspiration" />
          </div>
          ` : ''}

          ${hasSketchFront ? `
          <!-- Sketch Front Page -->
          <div class="image-page">
            <h2>SKETCH - FRONT VIEW</h2>
            <img src="${fullWO.article_data.sketch_front_image}" alt="Sketch Front" />
          </div>
          ` : ''}

          ${hasSketchBack ? `
          <!-- Sketch Back Page -->
          <div class="image-page">
            <h2>SKETCH - BACK VIEW</h2>
            <img src="${fullWO.article_data.sketch_back_image}" alt="Sketch Back" />
          </div>
          ` : ''}

          ${hasFinishedGood ? `
          <!-- Finished Good Page -->
          <div class="image-page">
            <h2>FINISHED GOOD IMAGE</h2>
            <img src="${fullWO.article_data.finished_good_image}" alt="Finished Good" />
          </div>
          ` : ''}
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for images to load before printing
    printWindow.onload = () => {
      // Wait a bit more for images
      setTimeout(() => {
        printWindow.print();
      }, 1500);
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Extract numeric part from WO number for search (e.g., "WO-I-0001" -> "1")
  const extractNumeric = (woNumber) => {
    const match = woNumber.match(/\d+/);
    return match ? match[0] : woNumber;
  };

  const getStatusCount = (status) => {
    return workOrders.filter(wo => wo.status === status).length;
  };

  const getFilteredWOs = () => {
    let filtered = workOrders;
    
    // If search term exists OR category filter is set, search across ALL statuses
    if (searchTerm.trim() !== '' || categoryFilter.trim() !== '') {
      // Apply search term filter if exists
      if (searchTerm.trim() !== '') {
        const searchNumeric = searchTerm.trim();
        filtered = filtered.filter(wo => {
          const woNumeric = extractNumeric(wo.wo_number);
          return woNumeric.includes(searchNumeric) || wo.wo_number.includes(searchTerm);
        });
      }
      
      // Apply category filter if set
      if (categoryFilter.trim() !== '') {
        filtered = filtered.filter(wo => wo.article_data?.category === categoryFilter);
      }
    } else {
      // If no search term AND no category filter, filter by selected status only
      filtered = filtered.filter(wo => wo.status === selectedStatus);
    }
    
    return filtered;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Internal Work Orders
              </h1>
              <p className="text-sm text-gray-600">Manage internal work orders for sampling</p>
            </div>
          </div>
          {canCreate('work_orders') && (
            <Button onClick={() => { setFormOpen(true); setFormError(''); }} className="gap-2 bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4" />
              New Internal Work Order
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Master Search and Category Filter */}
        <div className="mb-6 bg-white p-4 rounded-lg border-2 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Master Search - Search Across All Statuses</Label>
              <Input
                type="text"
                placeholder="Enter WO Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Filter by Article Category</Label>
              <SearchableSelect
                options={[
                  { value: "", label: "All Categories" },
                  ...Array.from(new Set(workOrders.map(wo => wo.article_data?.category).filter(Boolean))).map(cat => ({
                    value: cat,
                    label: cat
                  }))
                ]}
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value)}
                placeholder="All Categories"
              />
            </div>
          </div>
          {(searchTerm || categoryFilter) && (
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">
                  {searchTerm && `Searching: "${searchTerm}"`}
                  {searchTerm && categoryFilter && ' | '}
                  {categoryFilter && `Category: ${categoryFilter}`}
                </p>
                <p className="text-xs text-gray-600">
                  Found {getFilteredWOs().length} result{getFilteredWOs().length !== 1 ? 's' : ''} across all statuses
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('');
                }}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Status Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'PENDING', label: 'Pending', count: getStatusCount('PENDING') },
                { key: 'IN PROGRESS', label: 'In Progress', count: getStatusCount('IN PROGRESS') },
                { key: 'COMPLETED', label: 'Completed', count: getStatusCount('COMPLETED') }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setSelectedStatus(tab.key);
                    setSearchTerm('');
                    setCategoryFilter('');
                  }}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${selectedStatus === tab.key
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label} <span className="ml-2 text-gray-500">{tab.count}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">WO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {searchTerm || categoryFilter ? 'Date' : 
                    selectedStatus === 'PENDING' ? 'Date of Creation' :
                    selectedStatus === 'IN PROGRESS' ? 'Date Started' :
                    selectedStatus === 'COMPLETED' ? 'Date Completed' : 'Date'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredWOs().map(wo => {
                // Determine which date to display based on status
                let displayDate = '';
                if (wo.status === 'PENDING') {
                  displayDate = wo.created_at ? new Date(wo.created_at).toLocaleDateString() : 'N/A';
                } else if (wo.status === 'IN PROGRESS') {
                  displayDate = wo.issued_at ? new Date(wo.issued_at).toLocaleDateString() : 'N/A';
                } else if (wo.status === 'COMPLETED') {
                  displayDate = wo.completed_at ? new Date(wo.completed_at).toLocaleDateString() : 'N/A';
                } else if (wo.status === 'CANCELLED') {
                  displayDate = wo.created_at ? new Date(wo.created_at).toLocaleDateString() : 'N/A';
                }

                return (
                  <tr key={wo.id}>
                    <td className="px-6 py-4 text-sm font-medium">
                      <button
                        onClick={() => handleView(wo)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                      >
                        {wo.wo_number}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm">{wo.article_code || '-'}</td>
                    <td className="px-6 py-4 text-sm">{wo.article_data?.category || '-'}</td>
                    <td className="px-6 py-4 text-sm">{displayDate}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs ${getStatusColor(wo.status)}`}>
                        {wo.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {hasPermission('work_orders_view') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintWO(wo)}
                            className="text-blue-700 border-blue-700 hover:bg-blue-50"
                          >
                            Print
                          </Button>
                        )}
                        {canDelete('work_orders') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(wo)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {getFilteredWOs().length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No work orders found in {selectedStatus} status</p>
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              New Internal Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Error Message Box */}
            {formError && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-red-800 uppercase tracking-wider">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{formError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* WO Type - Fixed Display */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Work Order Type</Label>
              <div className="mt-1 px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-md">
                <p className="text-sm font-semibold">Internal Work Order</p>
                <p className="text-xs text-gray-500 mt-1">Auto-generated format: WO-I-XXXX</p>
              </div>
            </div>

            {/* LN-X Code Selection */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Select LN-X Code</Label>
              <SearchableSelect
                options={articleOptions}
                value={selectedArticle?.article_code || ''}
                onChange={handleArticleSelect}
                placeholder="Search and select article..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Select an article from Fashion Design</p>
            </div>

            {/* Display Article Details */}
            {loadingArticle && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Loading article details...</p>
              </div>
            )}

            {selectedArticle && !loadingArticle && (
              <div className="space-y-4 border-2 border-black rounded-lg p-6 max-h-[600px] overflow-y-auto">
                {/* Header with Article Code and Barcode */}
                <div className="flex justify-between items-start pb-4 border-b-2 border-black">
                  <div>
                    <h3 className="font-bold text-xl uppercase tracking-wider" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {selectedArticle.article_code}
                    </h3>
                    <p className="text-sm text-gray-600 uppercase">{selectedArticle.category || 'Article Details'}</p>
                  </div>
                  {selectedArticle.article_barcode && (
                    <img src={selectedArticle.article_barcode} alt="Article Barcode" className="w-40" />
                  )}
                </div>
                
                {/* Fabrics Section - Detailed for Inventory Issuance */}
                {selectedArticle.fabrics && selectedArticle.fabrics.length > 0 && (
                  <div className="border-2 border-black rounded p-4">
                    <p className="text-sm font-bold uppercase text-gray-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      FABRICS
                    </p>
                    <div className="space-y-3">
                      {selectedArticle.fabrics.map((fabric, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-300 rounded p-3">
                          <div className="grid grid-cols-4 gap-3 text-xs mb-2">
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Code</p>
                              <p className="font-bold text-sm">{fabric.fabric_code || fabric.code}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Color Code</p>
                              <p className="font-bold text-sm">{fabric.color_code}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Color Name</p>
                              <p className="font-bold text-sm">{fabric.color_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Consumption</p>
                              <p className="font-bold text-sm text-blue-600">{fabric.consumption} M</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-xs pt-2 border-t border-gray-300">
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Composition</p>
                              <p className="text-sm">{fabric.composition || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Width / GSM</p>
                              <p className="text-sm">{fabric.width} / {fabric.gsm}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Type</p>
                              <p className="text-sm">{fabric.fabric_type || '-'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trims & Accessories Section - Detailed for Inventory Issuance */}
                {selectedArticle.trims && selectedArticle.trims.length > 0 && (
                  <div className="border-2 border-black rounded p-4">
                    <p className="text-sm font-bold uppercase text-gray-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      TRIMS & ACCESSORIES
                    </p>
                    <div className="space-y-2">
                      {selectedArticle.trims.map((trim, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-300 rounded p-3">
                          <div className="grid grid-cols-4 gap-3 text-xs">
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Code</p>
                              <p className="font-bold text-sm">{trim.trim_code || trim.code}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Color Code</p>
                              <p className="font-bold text-sm">{trim.color_code}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Color Name</p>
                              <p className="font-bold text-sm">{trim.color_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Quantity</p>
                              <p className="font-bold text-sm text-blue-600">QTY {trim.quantity}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Packaging Section */}
                {selectedArticle.packaging && selectedArticle.packaging.length > 0 && (
                  <div className="border-2 border-black rounded p-4">
                    <p className="text-sm font-bold uppercase text-gray-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      PACKAGING
                    </p>
                    <div className="space-y-2">
                      {selectedArticle.packaging.map((pkg, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-300 rounded p-3">
                          <div className="grid grid-cols-3 gap-3 text-xs">
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Code</p>
                              <p className="font-bold text-sm">{pkg.packaging_code || pkg.code}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Description</p>
                              <p className="text-sm">{pkg.description}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase font-semibold">Quantity</p>
                              <p className="font-bold text-sm text-blue-600">{pkg.quantity} units</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Points of Measurement (POM) */}
                {selectedArticle.poms && selectedArticle.poms.length > 0 && (
                  <div className="border-2 border-black rounded p-4">
                    <p className="text-sm font-bold uppercase text-gray-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      POINTS OF MEASUREMENT
                    </p>
                    <div className="space-y-2">
                      {selectedArticle.poms.map((pom, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 px-3 bg-gray-50 border border-gray-200 rounded">
                          <div className="flex gap-4">
                            <p className="text-xs font-semibold uppercase">SIZE {pom.size}</p>
                            <p className="text-xs uppercase">{pom.point_name}</p>
                          </div>
                          <p className="text-sm font-bold">{pom.measurement} {pom.uom}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status */}
                {selectedArticle.status && (
                  <div className="border-2 border-black rounded p-4 bg-black text-white">
                    <p className="text-xs font-bold uppercase tracking-wider mb-1">CURRENT STATUS</p>
                    <p className="text-2xl font-bold uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {selectedArticle.status}
                    </p>
                  </div>
                )}

                {/* Images */}
                {selectedArticle.images && selectedArticle.images.length > 0 && (
                  <div className="border-2 border-black rounded p-4">
                    <p className="text-sm font-bold uppercase text-gray-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      ARTICLE IMAGES
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedArticle.images.map((img, idx) => (
                        <img key={idx} src={img} alt={`Article ${idx + 1}`} className="w-full h-24 object-cover rounded border border-gray-300" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter work order description"
                rows={3}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => {
                setFormOpen(false);
                setSelectedArticle(null);
                setDescription('');
              }} variant="outline" className="flex-1" disabled={savingWO}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                className="flex-1 bg-black hover:bg-gray-800"
                disabled={savingWO || !selectedArticle}
              >
                {savingWO ? 'Creating...' : 'Create Work Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Work Order Details
              </DialogTitle>
            </div>
          </DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              {/* PAGE 1: ALL ARTICLE DETAILS - No page breaks inside this section */}
              <div style={{ pageBreakAfter: 'always', pageBreakInside: 'avoid' }}>
                {/* Header with Barcode */}
                <div className="p-5 border-2 border-black rounded mb-4">
                  <div className="flex justify-between items-start">
                    <div className="grid grid-cols-2 gap-6 flex-1">
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">WO NUMBER</p>
                        <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{viewDetails.wo_number}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">TYPE</p>
                        <p className="text-sm">{viewDetails.wo_type}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">ARTICLE CODE</p>
                        <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{viewDetails.article_code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">STATUS</p>
                        <p className="text-sm">{viewDetails.status}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">CREATED</p>
                        <p className="text-sm">{new Date(viewDetails.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">CREATED BY</p>
                        <p className="text-sm">{viewDetails.created_by || '-'}</p>
                      </div>
                    </div>
                    {viewDetails.wo_barcode && (
                      <div className="ml-6">
                        <img src={viewDetails.wo_barcode} alt="WO Barcode" className="w-48" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="p-5 border-2 border-black rounded mb-4">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-2">DESCRIPTION</p>
                  <p className="text-sm">{viewDetails.description}</p>
                </div>

              {/* FABRICS SECTION */}
              {viewDetails.items && viewDetails.items.filter(item => item.type === 'FABRIC').length > 0 && (
                <div className="p-5 border-2 border-black rounded mb-4">
                  <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    FABRICS REQUIRED FOR INVENTORY ISSUANCE
                  </p>
                  <div className="space-y-3">
                    {viewDetails.items.filter(item => item.type === 'FABRIC').map((item, idx) => (
                      <div key={idx} className="bg-gray-50 border-2 border-gray-300 rounded p-4">
                        <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Fabric Code</p>
                            <p className="font-bold text-base">{item.code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Color Code</p>
                            <p className="font-bold text-base">{item.color_code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Color Name</p>
                            <p className="font-bold text-base">{item.color_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Consumption</p>
                            <p className="font-bold text-lg">{item.consumption} {item.unit}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm pt-3 border-t border-gray-300">
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Composition</p>
                            <p className="text-sm">{item.composition || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Width / GSM</p>
                            <p className="text-sm">{item.width ? `${item.width} / ${item.gsm}` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Fabric Type</p>
                            <p className="text-sm">{item.fabric_type || '-'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TRIMS SECTION */}
              {viewDetails.items && viewDetails.items.filter(item => item.type === 'TRIM').length > 0 && (
                <div className="p-5 border-2 border-black rounded mb-4">
                  <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    TRIMS & ACCESSORIES REQUIRED FOR INVENTORY ISSUANCE
                  </p>
                  <div className="space-y-3">
                    {viewDetails.items.filter(item => item.type === 'TRIM').map((item, idx) => (
                      <div key={idx} className="bg-gray-50 border-2 border-gray-300 rounded p-4">
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Trim Code</p>
                            <p className="font-bold text-base">{item.code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Color Code</p>
                            <p className="font-bold text-base">{item.color_code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Color Name</p>
                            <p className="font-bold text-base">{item.color_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Quantity</p>
                            <p className="font-bold text-lg">QTY {item.quantity}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PACKAGING SECTION */}
              {viewDetails.items && viewDetails.items.filter(item => item.type === 'PACKAGING').length > 0 && (
                <div className="p-5 border-2 border-black rounded mb-4">
                  <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    PACKAGING REQUIRED
                  </p>
                  <div className="space-y-2">
                    {viewDetails.items.filter(item => item.type === 'PACKAGING').map((item, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-300 rounded p-3 flex justify-between items-center">
                        <div className="flex gap-6">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Code</p>
                            <p className="font-bold">{item.code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Description</p>
                            <p className="text-sm">{item.description}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Quantity</p>
                          <p className="font-bold">{item.quantity} {item.unit}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* POINTS OF MEASUREMENT (POM) - For Samplers */}
              {viewDetails.article_data && viewDetails.article_data.poms && viewDetails.article_data.poms.length > 0 && (
                <div className="p-5 border-2 border-black rounded" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    POINTS OF MEASUREMENT (FOR SAMPLER)
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {viewDetails.article_data.poms.map((pom, idx) => (
                      <div key={idx} className="flex justify-between items-center py-3 px-4 bg-gray-50 border-2 border-gray-300 rounded" style={{ pageBreakInside: 'avoid' }}>
                        <div className="flex gap-4">
                          <p className="text-xs font-bold uppercase">SIZE {pom.size}</p>
                          <p className="text-xs uppercase font-semibold">{pom.point_name}</p>
                        </div>
                        <p className="text-base font-bold">{pom.measurement} {pom.uom}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* COMMENTS FROM DESIGN HQ */}
              {viewDetails.article_data && viewDetails.article_data.comments && (
                <div className="p-5 border-2 border-black rounded" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    COMMENTS
                  </p>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{viewDetails.article_data.comments}</p>
                </div>
              )}
              </div>

              {/* DESIGN IMAGES SECTION */}
              {viewDetails.article_data && (
                <div>
                  {/* INSPIRATION IMAGE - Separate Page */}
                  {viewDetails.article_data.inspiration_image && (
                    <div className="pdf-page-break p-5 border-2 border-black rounded bg-white">
                      <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        INSPIRATION IMAGE
                      </p>
                      <div className="flex items-center justify-center py-4">
                        <img 
                          src={viewDetails.article_data.inspiration_image} 
                          alt="Inspiration" 
                          className="object-contain border-2 border-gray-300"
                          style={{ maxWidth: '100%', maxHeight: '600px' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* SKETCH FRONT IMAGE - Separate Page */}
                  {viewDetails.article_data.sketch_front_image && (
                    <div className="pdf-page-break p-5 border-2 border-black rounded bg-white">
                      <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        SKETCH - FRONT VIEW
                      </p>
                      <div className="flex items-center justify-center py-4">
                        <img 
                          src={viewDetails.article_data.sketch_front_image} 
                          alt="Sketch Front" 
                          className="object-contain border-2 border-gray-300"
                          style={{ maxWidth: '100%', maxHeight: '600px' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* SKETCH BACK IMAGE - Separate Page */}
                  {viewDetails.article_data.sketch_back_image && (
                    <div className="pdf-page-break p-5 border-2 border-black rounded bg-white">
                      <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        SKETCH - BACK VIEW
                      </p>
                      <div className="flex items-center justify-center py-4">
                        <img 
                          src={viewDetails.article_data.sketch_back_image} 
                          alt="Sketch Back" 
                          className="object-contain border-2 border-gray-300"
                          style={{ maxWidth: '100%', maxHeight: '600px' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* FINISHED GOOD IMAGE - Separate Page */}
                  {viewDetails.article_data.finished_good_image && (
                    <div className="pdf-page-break p-5 border-2 border-black rounded bg-white">
                      <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        FINISHED GOOD IMAGE
                      </p>
                      <div className="flex items-center justify-center py-4">
                        <img 
                          src={viewDetails.article_data.finished_good_image} 
                          alt="Finished Good" 
                          className="object-contain border-2 border-gray-300"
                          style={{ maxWidth: '100%', maxHeight: '600px' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* No Images Message */}
                  {!viewDetails.article_data.inspiration_image && 
                   !viewDetails.article_data.sketch_front_image && 
                   !viewDetails.article_data.sketch_back_image && 
                   !viewDetails.article_data.finished_good_image && (
                    <div className="p-5 border-2 border-black rounded bg-yellow-50">
                      <p className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        DESIGN IMAGES
                      </p>
                      <p className="text-sm text-gray-600">No design images uploaded yet for article {viewDetails.article_code}. Please upload images in Fashion Design module.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={handleDeleteConfirm}
        title="Delete Work Order"
        message={woToDelete ? `Enter authorization code to delete work order ${woToDelete.wo_number}` : 'Enter authorization code to proceed'}
      />
    </div>
  );
}
